import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Page } from '@/shared/ui/Page';
import { PageHeader } from '@/shared/ui/PageHeader';
import { GreenLiquidGlassButton } from '@/shared/ui/GreenLiquidGlassButton';
import { subscriptionApi } from '@/entities/subscription/api';
import { amountText, toNumber } from '@/shared/lib/money';
import { cn } from '@/shared/lib/cn';
import { useT } from '@/shared/lib/i18n';

type CloseDate = 'period_end' | 'today' | 'custom';

export function CloseSubscription() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const t = useT();
  const [mode, setMode] = useState<CloseDate>('period_end');
  const [customDate, setCustomDate] = useState(() => new Date().toISOString().slice(0, 10));

  const detailQ = useQuery({
    queryKey: ['subscription', id],
    queryFn: () => subscriptionApi.getDetail(id!),
    enabled: !!id,
  });
  const membersQ = useQuery({
    queryKey: ['subscription-members', id],
    queryFn: () => subscriptionApi.getMembers(id!),
    enabled: !!id,
  });

  const closeM = useMutation({
    mutationFn: () => subscriptionApi.close(id!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscription', id] });
      await qc.invalidateQueries({ queryKey: ['subscriptions', 'holder'] });
      navigate('/');
    },
  });

  const sub = detailQ.data?.subscription;
  const members = membersQ.data?.items ?? [];
  const modes: { value: CloseDate; label: string; hint: string }[] = [
    { value: 'period_end', label: t('management.close.periodEnd'), hint: sub?.billing_date ?? t('common.dash') },
    { value: 'today', label: t('management.scenario.today'), hint: new Date().toISOString().slice(0, 10) },
    { value: 'custom', label: t('management.close.custom'), hint: '▾' },
  ];

  return (
    <Page>
      <PageHeader title={<span className="font-bold text-[19px]">{t('management.close.title')}</span>} />
      <div className="px-[18px] pb-5 flex flex-col gap-4 min-h-0">
        <div>
          <p className="text-[14px] font-semibold text-text-secondary mb-2">{t('management.close.lastDay')}</p>
          <div className="flex flex-col gap-2">
            {modes.map((item) => (
              <button
                key={item.value}
                onClick={() => setMode(item.value)}
                className={cn(
                  'flex items-center gap-3 rounded-[12px] px-4 py-3 text-[14px] bg-white/50 border',
                  mode === item.value ? 'border-2 border-brand-green bg-brand-green/10' : 'border-white/60',
                )}
              >
                <span className={cn('w-[17px] h-[17px] rounded-full border-2', mode === item.value ? 'border-brand-green bg-brand-green' : 'border-[#b9c4bb]')} />
                {item.label}
                <span className="ml-auto text-[12px] text-text-muted">{item.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {mode === 'custom' && (
          <input
            type="date"
            value={customDate}
            onChange={(event) => setCustomDate(event.target.value)}
            className="rounded-[12px] px-4 py-3 text-[15px] bg-white/55 border border-white/60"
          />
        )}

        <div className="rounded-[13px] p-3 bg-brand-green/10 border border-brand-green/25 text-[13px] text-[#1c5a4a]">
          {t('management.close.preview')}
        </div>

        <p className="text-[14px] font-semibold text-text-secondary">{t('management.close.finalBalances')}</p>
        <div className="rounded-[16px] px-4 py-1 bg-[rgba(244,251,245,0.5)] border border-white/55">
          {members.map((member) => {
            const balance = toNumber(member.balance) ?? 0;
            return (
              <div key={member.membership_id} className="flex justify-between py-3 border-b border-[rgba(18,52,36,0.08)] last:border-0 text-[15px]">
                <span>{member.display_name}</span>
                <span className={cn('font-mono font-semibold', balance > 0 ? 'text-status-debt' : balance < 0 ? 'text-status-credit' : 'text-status-paid')}>
                  {amountText(balance, sub?.currency ?? 'RUB')}
                </span>
              </div>
            );
          })}
          {!membersQ.isLoading && members.length === 0 && (
            <p className="text-center text-[14px] text-text-secondary py-6">{t('subscription.membersEmpty')}</p>
          )}
        </div>

        <div className="mt-auto">
          {closeM.isError && (
            <p className="text-[13px] text-status-debt text-center mb-2">{t('management.close.error')}</p>
          )}
          <GreenLiquidGlassButton
            loading={closeM.isPending}
            className="!text-status-debt"
            onClick={() => closeM.mutate()}
          >
            {t('management.close.submit')}
          </GreenLiquidGlassButton>
        </div>
      </div>
    </Page>
  );
}
